/* istanbul ignore file */
import { navContentStore } from "@phn/state/nav-content-store";
import { Fragment, h } from "@stencil/core";
import { getMenuContent } from "@phn/services/menu-items-service";
import { isDesktop } from "@phn/utility/helper";
import { GlobalDataLayerService } from "@phn/services/analytics/global-data-layer-service";
import {
  ClickElementTypes,
  ElementTypes,
  EventActions,
} from "@phn/utility/constants";
import analyticsManager from "@phn/services/analytics/analytics-manager";
import { getCurrentRoute } from "@phn/services/routing-manager";
import {
  Series,
  TMenu,
  TVehiclePurchaseMenu,
} from "../../../../../shared/entities/content";

interface ElectricOwnershipTypes {
  activeRoutingKey: string;
  topLevelRoutingKey: string;
  breakpoint: number;
  receivedContent: boolean;
}

export default function ElectricOwnership({
  activeRoutingKey,
  topLevelRoutingKey,
  breakpoint,
  receivedContent,
}: ElectricOwnershipTypes): HTMLElement {
  const showHeadline = !isDesktop(breakpoint);
  const displayedContent = getMenuContent(
    navContentStore.state,
    activeRoutingKey,
    topLevelRoutingKey,
  ) as TVehiclePurchaseMenu;

  /* istanbul ignore if */
  if (displayedContent === null) {
    return <div></div>;
  }

  const tilesIndex = displayedContent.children.findIndex((item) =>
    item.id.endsWith(".all"),
  );
  const tiles = displayedContent.children[tilesIndex] as TVehiclePurchaseMenu;

  return (
    <Fragment>
      {tilesIndex === 0 ? (
        <TileComponent
          tiles={tiles}
          series={navContentStore.state.models.series}
          isMobile={showHeadline}
        />
      ) : null}

      <phn-nd-menu
        item={cloneWithoutId(
          displayedContent,
          "mainmenu.electricOwnership.all",
        )}
        showHeadline={showHeadline}
        receivedContent={receivedContent}
      />

      {tilesIndex !== 0 ? (
        <TileComponent
          tiles={tiles}
          series={navContentStore.state.models.series}
          isMobile={showHeadline}
        />
      ) : null}
    </Fragment>
  );
}

function TileComponent({
  tiles,
  isMobile,
  series,
}: {
  tiles: TVehiclePurchaseMenu;
  isMobile: boolean;
  series: Series[];
}) {
  return (
    <div class="electric-ownership">
      <phn-p-text size="inherit" color="contrast-medium">
        {tiles.text}
      </phn-p-text>

      {tiles.children.map((tile) => {
        let image = series.find(
          (s) => s.image === tile.id.split(".").at(-1),
        )?.modelCompareImage;

        if (image) {
          const url = `${new URL(image).toString()}/m/0x${String(Math.round(window.devicePixelRatio * 510))}`;
          image = url.toString();
        }

        return (
          <phn-p-link-tile
            aria-label={tile.text}
            href={tile.link}
            onClick={() => trackModelTileClick(tile)}
            description={tile.text}
            key={tile.id}
            compact={true}
            size={isMobile ? "large" : "medium"}
            aspect-ratio="16/9"
            tabindex="0"
          >
            {image ? <img src={image} alt={tile.text} /> : null}
          </phn-p-link-tile>
        );
      })}
    </div>
  );
}

function trackModelTileClick(tile: TMenu): void {
  GlobalDataLayerService.getInstance().pushDataLayerEvent(
    EventActions.MODEL_CLICK,
    {
      componentClick: {
        clickElementId: tile.id,
        clickElementName: tile.text,
        clickElementType: ClickElementTypes.Link,
        targetUrl: tile.link,
      },
      componentDisplay: {
        displayElementId: analyticsManager.calculateDisplayElementId(),
        displayElementType: analyticsManager.calculateDisplayElementType(
          getCurrentRoute(),
          ElementTypes.bodyTypeButton,
        ),
        displayElementName: tile.text,
      },
    },
  );
}

function cloneWithoutId(node, excludedId) {
  if (node.id === excludedId) return null;

  const cloned = { ...node };

  if (cloned.children && cloned.children.length > 0) {
    cloned.children = cloned.children
      .map((child) => cloneWithoutId(child, excludedId))
      .filter(Boolean); // remove nulls
  }

  return cloned;
}
